import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { Toaster } from "./components/ui/sonner";

// Client Pages
import SplashScreen from "./pages/SplashScreen";
import AuthPage from "./pages/AuthPage";
import HomePage from "./pages/HomePage";
import AIAssistantPage from "./pages/AIAssistantPage";
import CatalogPage from "./pages/CatalogPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import OrdersPage from "./pages/OrdersPage";
import OrderDetailPage from "./pages/OrderDetailPage";
import LoyaltyPage from "./pages/LoyaltyPage";
import OfflineBanner from "./components/OfflineBanner";
import FuelStationsPage from "./pages/FuelStationsPage";

// Funcionario Pages
import FuncionarioAuthPage from "./pages/FuncionarioAuthPage";
import AdminDashboard from "./pages/AdminDashboard";

// Delivery Driver Pages
import DeliveryAuthPage from "./pages/DeliveryAuthPage";
import DeliveryDashboard from "./pages/DeliveryDashboard";

// Password Recovery Pages
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";

// Super Admin Pages
import SuperAdminLogin from "./pages/SuperAdminLogin";
import SuperAdminDashboard from "./pages/SuperAdminDashboard";

// Client Protected Route - Only for customers
const ClientRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-100">
        <div className="animate-spin w-8 h-8 border-4 border-sonangol-red border-t-transparent rounded-full"></div>
      </div>
    );
  }
  
  if (!user) return <Navigate to="/auth" replace />;
  // If funcionario tries to access client pages, redirect to admin
  if (user.role === 'station_admin') return <Navigate to="/admin" replace />;
  
  return children;
};

// Funcionario Protected Route - Only for station admins
const FuncionarioRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-900">
        <div className="animate-spin w-8 h-8 border-4 border-sonangol-orange border-t-transparent rounded-full"></div>
      </div>
    );
  }
  
  if (!user) return <Navigate to="/funcionario" replace />;
  // If customer tries to access funcionario pages, redirect to home
  if (user.role !== 'station_admin') return <Navigate to="/" replace />;
  
  return children;
};

function AppRoutes() {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <SplashScreen />;
  }
  
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/splash" element={<SplashScreen />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* Client Auth - redirect if already logged in */}
      <Route path="/auth" element={
        user 
          ? (user.role === 'station_admin' ? <Navigate to="/admin" replace /> : <Navigate to="/" replace />) 
          : <AuthPage />
      } />
      
      {/* Funcionario Auth - separate entry point */}
      <Route path="/funcionario" element={
        user?.role === 'station_admin' 
          ? <Navigate to="/admin" replace /> 
          : <FuncionarioAuthPage />
      } />
      
      {/* ========== CLIENT ROUTES ========== */}
      <Route path="/" element={<ClientRoute><HomePage /></ClientRoute>} />
      <Route path="/assistant" element={<ClientRoute><AIAssistantPage /></ClientRoute>} />
      <Route path="/catalog" element={<ClientRoute><CatalogPage /></ClientRoute>} />
      <Route path="/product/:id" element={<ClientRoute><ProductDetailPage /></ClientRoute>} />
      <Route path="/cart" element={<ClientRoute><CartPage /></ClientRoute>} />
      <Route path="/checkout" element={<ClientRoute><CheckoutPage /></ClientRoute>} />
      <Route path="/orders" element={<ClientRoute><OrdersPage /></ClientRoute>} />
      <Route path="/order/:id" element={<ClientRoute><OrderDetailPage /></ClientRoute>} />
      <Route path="/loyalty" element={<ClientRoute><LoyaltyPage /></ClientRoute>} />
      <Route path="/fuel-stations" element={<ClientRoute><FuelStationsPage /></ClientRoute>} />
      
      {/* ========== FUNCIONARIO ROUTES ========== */}
      <Route path="/admin" element={<FuncionarioRoute><AdminDashboard /></FuncionarioRoute>} />
      <Route path="/admin/order/:id" element={<FuncionarioRoute><OrderDetailPage /></FuncionarioRoute>} />
      
      {/* ========== DELIVERY DRIVER ROUTES ========== */}
      <Route path="/delivery" element={<DeliveryAuthPage />} />
      <Route path="/delivery/dashboard" element={<DeliveryDashboard />} />
      
      {/* ========== SUPER ADMIN ROUTES ========== */}
      <Route path="/admin/login" element={<SuperAdminLogin />} />
      <Route path="/super-admin/dashboard" element={<SuperAdminDashboard />} />
      
      {/* Default redirect */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <AppRoutes />
          <Toaster position="top-center" richColors />
          <OfflineBanner />
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
